import Tone from 'tone';

export default superclass => class extends superclass  {
    triggerAttack(note, time, velocity, start, stepData) {
        if (!this.loaded && !Tone.Master.context.isOffline) {
            return;
        }

        time = Tone.Master.context.isOffline ?
            Tone.Master.context._currentTime :
            Tone.Master.context.currentTime; 

        // find a voice to use
        let voice = this.useVoice(note, time);
        if (!voice) {
            return;
        }
        let {voiceNumber} = voice;
        this.voicesUsed.push(voiceNumber);

        // based on the envelopes of each oscillator, set the
        // underlying oscillator envelopes
        this.setEnvelopes(stepData, note, voice);
        this.triggerAttackEnvelopes(time, velocity, voice.slotNumber);

        // scale any parameters by the note being played 
        this.scaleByKey(time, note, voiceNumber);

        // trigger attack the carrier oscillator
        this.carrierOscillator
            .triggerAttack(note, time, velocity, voice.carrier);

        // trigger attack each modulator
        voice.modulators
            .filter((s, i) => this.oscillatorNodes[i].enabled)
            .forEach(voice => {
                voice._triggerEnvelopeAttack(time, velocity);
            });

        this.storeVoiceFrequency(note, voiceNumber, voice);
    }

    triggerAttackRelease(note, duration, time, velocity, start, stepData) {
        if (!this.loaded && !Tone.Master.context.isOffline) {
            return;
        }

        time = new Tone.Time(time);

        if (velocity > 1) {
            velocity = 1;
        }

        let voice = this.useVoice(note, time);
        if (!voice) {
            return;
        }
       
        let {voiceNumber} = voice;
        this.voicesUsed.push(voiceNumber);

        this.setEnvelopes(stepData, undefined, voice);
        this.triggerAttackReleaseEnvelopes(duration, time, velocity, voice.slotNumber);
        this.scaleByKey(time, note, voiceNumber);

        
        // trigger each oscillator
        this.carrierOscillator
            .triggerAttackRelease(
                note, duration, time, velocity, voice.carrier);

        voice.modulators
            .filter((s, i) => this.oscillatorNodes[i].enabled)
            .forEach(
                voice => {
		    voice._triggerEnvelopeAttack(time, velocity);
		    voice._triggerEnvelopeRelease(time + duration);
                });

        this.storeVoiceFrequency(note, voiceNumber, voice);
        this.lastTranspose = note;
    }

    triggerRelease(notes, time=Tone.Master.context.currentTime, releaseTime) {
        // trigger filter envelope
        let freqs = notes && notes.map(
            transpose =>
                this.oscillators[0].getNoteFromTranspose(transpose));

        let freq = freqs[0].toFrequency();
        let voiceNumber = notes === undefined ? 0 : this.frequencyToVoice[freq];
        // trigger each oscillator

        if (this.voiceToFrequency[voiceNumber] !== freq) {
            // note has been stolen so dont relesae
            return;
        }

        let voice = this.numberToVoice[voiceNumber];

        this.setEnvelopes(undefined, undefined, voice);

        this.triggerReleaseEnvelopes(time, voice.slotNumber, releaseTime);

        this.voiceToTranspose[voiceNumber] = notes && notes[0];

        if (releaseTime === undefined) {
            this.carrierOscillator
                .triggerRelease(notes, voice.carrier, time, releaseTime);
        }

        voice.modulators.forEach(
                voice => {
                    voice
                        ._triggerEnvelopeRelease(time, releaseTime);
                });

    }

    isLastVoice(time) {
        let voices = this.oscillators[0].voices;
        let _c = this.voiceCounter;
        let counts=0;
        for (let i=0; i < this.polyphonyParameter.get(); i++) {
            let envValue = voices[i].envelope.getValueAtTime(time);
            if (envValue > 0) {
                counts++;
            }
            if (counts > 1) {
                return false;
            }
        }
        return true;
    }

    releaseAll() {
        if (this.disposed) {
            return;
        }
        for (let osc of this.oscillatorNodes) {
            osc._voices.forEach(v => v.envelope.release = 0.01);
            osc._voices.forEach(v => v.triggerRelease());
        }

        this.clearSlide();
        this.releaseSlide();
        this.detuneSignal.value = this.detuneParameter.get(true);
        this.lastTranspose = 0;
        this.clearVoiceData(false);
    }

    // sets the "amp envelopes" for the oscillators.
    // called when synth is triggered
    setEnvelopes(stepData, transpose, voice) {
        // first refresh the pitch and filter envelopes (in case they
        // are being modulated)
        this.ampEnvelope.refresh();
        this.pitchEnvelope.refresh();
        this.filterEnvelope.refresh();
        this.hpFilterEnvelope.refresh();

        this.carrierOscillator.setEnvelopes(
            stepData, this.ampEnvelope, transpose, voice.carrier);
        this.modulatorOscillators.forEach(
            (osc, i) => 
                osc.setEnvelopes(stepData, this.ampEnvelope, transpose, voice.modulators[i]));
    }

    triggerAttackEnvelopes(time, velocity, slotNumber) {
        this.forSlot(slotNumber).forEach(
            ({envelope}) => envelope.triggerAttack(time, velocity));

        this.filterEnvelope.triggerAttack(time, velocity);
        this.hpFilterEnvelope.triggerAttack(time, velocity);
        this.pitchEnvelope.envelope.triggerAttack(time, velocity);
    }

    triggerReleaseEnvelopes(time, slotNumber, releaseTime) {
        this.forSlot(slotNumber).forEach(
            ({envelope}) => envelope.triggerRelease(
                time + (1 / Tone.Master.context.sampleRate), releaseTime));

        if (this.isLastVoice(time)) {
            this.filterEnvelope.triggerRelease(time);
            this.hpFilterEnvelope.triggerRelease(time);
            this.pitchEnvelope.envelope.triggerRelease(time);
        }
    }

    triggerAttackReleaseEnvelopes(duration, time, velocity, slotNumber) {
        this.forSlot(slotNumber).forEach(
            ({envelope}) => envelope.triggerAttackRelease(
                duration, time, velocity));

        this.filterEnvelope.triggerAttackRelease(duration, time, velocity);
        this.hpFilterEnvelope.triggerAttackRelease(duration, time, velocity);
        this.pitchEnvelope.envelope.triggerAttackRelease(duration, time, velocity);
    }
}
